import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineDelete, MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { TmdxAvatar } from '@ds';
import { ActionMenu, MenuItemType, TableColumnType } from '@ui';
import { LogisticProvider, LogisticProviderListFilters } from '@/api/apis/facilities';
import { RegionCoverageCell } from './components/RegionCoverageCell';
import { getFileUrl } from '@/api/utils';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';
import { NA_REGION_ID, EUROPE_REGION_ID } from '@/constants/serviceRegion.constants';

export const LOGISTIC_PROVIDERS_LIST_FILTERS_BASE: LogisticProviderListFilters = {
  countries: [],
  states: [],
  cities: [],
  ids: [],
  serviceRegionIds: '',
};

export const LOGISTIC_PROVIDERS_DISABLED_FILTER_KEYS: (keyof LogisticProviderListFilters)[] = [];

export const FILTERS_NAMES: Record<string, string> = {
  countries: 'Country Coverage',
  serviceRegionId: 'Service Region',
  states: 'State',
};

export const LOGISTIC_PROVIDER_TABLE_COLUMNS: TableColumnType[] = [
  col('name', 'Provider name', {
    sortable: true,
    minWidthClassName: 'min-w-56',
  }),
  col('naCoverage', 'North America Coverage', {
    sortable: false,
    minWidthClassName: 'min-w-52',
  }),
  col('europeCoverage', 'Europe Coverage', {
    sortable: false,
    minWidthClassName: 'min-w-44',
  }),
  col('primaryPhone', 'Primary Phone', {
    sortable: false,
    minWidthClassName: 'min-w-44',
  }),
  col('aircraftType', 'Aircraft type', {
    sortable: false,
    minWidthClassName: 'min-w-44',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

const getCreateActionsMenu = (onEdit: () => void, onDelete: () => void): MenuItemType[] => [
  {
    key: 'edit-logistic-provider',
    type: 'action',
    to: 'edit-logistic-provider',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
  {
    key: 'delete-logistic-provider',
    type: 'action',
    to: 'delete-logistic-provider',
    label: (
      <div className="flex items-center gap-2 text-danger">
        <MdOutlineDelete size={16} />
        <span className="text-m-r">Delete</span>
      </div>
    ),
    onAction: () => onDelete(),
  },
];

const getEditActionsMenu = (onEdit: () => void): MenuItemType[] => [
  {
    key: 'edit-logistic-provider',
    type: 'action',
    to: 'edit-logistic-provider',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
];

export const getRenderLogisticProviderTableCell =
  (
    onEdit: (logisticProviderId: string) => void,
    onDelete: (hospitalId: string) => void,
    isPartnerProvider: boolean,
    hasCreatePermissions: boolean,
  ) =>
  (logisticProvider: LogisticProvider, columnKey: Key): ReactNode => {
    const { id, name, logoPath, coverage, primaryPhone, aircraftType } = logisticProvider;

    const onLogisticProviderEdit = () => onEdit(id);
    const onLogisticProviderDelete = () => onDelete(id);

    const ACTIONS_MENU_CREATE = getCreateActionsMenu(onLogisticProviderEdit, onLogisticProviderDelete);
    const ACTIONS_MENU_EDIT = getEditActionsMenu(onLogisticProviderEdit);

    switch (columnKey) {
      case 'name':
        return (
          <Link
            to={`/admin/logistics_providers/${isPartnerProvider ? 'supplemental_lift' : 'tx_center'}/${id}`}
            className="flex items-center gap-2"
          >
            <TmdxAvatar radius="xl" size="xl" type="transport" src={getFileUrl(logoPath)} />
            <span className="text-m-m">{name}</span>
          </Link>
        );
      case 'naCoverage':
        return <RegionCoverageCell coverage={coverage} serviceRegionId={NA_REGION_ID} />;
      case 'europeCoverage':
        return <RegionCoverageCell coverage={coverage} serviceRegionId={EUROPE_REGION_ID} />;
      case 'primaryPhone':
        return <span className="text-m-r">{primaryPhone}</span>;
      case 'aircraftType':
        return <span className="text-m-r">{aircraftType || '-'}</span>;
      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={hasCreatePermissions ? ACTIONS_MENU_CREATE : ACTIONS_MENU_EDIT}
            MenuTrigger={
              <div className="">
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label={`${name} actions menu`}
          />
        );
      default:
        return null;
    }
  };
